/**
 * AIHelperPanel — displays the AI helper response.
 *
 * Mobile: bottom sheet sliding up from the bottom.
 * Desktop: side panel sliding in from the right.
 *
 * Flow:
 *   1. User highlights text -> panel opens in confirmation mode
 *   2. User clicks confirm -> typing dots (Lella is thinking...)
 *   3. Full response arrives -> rendered as Markdown in a chat bubble from Lella
 */

import React, { useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useAIHelper } from '../../contexts/AIHelperContext';
import { useGettoncini } from '../../contexts/GettonciniContext';
import useHasEverAuthenticated from '../../hooks/useHasEverAuthenticated';
import GettonciniCostChip from '../GettonciniCostChip';
import RewardedGettonciniCard from '../RewardedGettonciniCard';
import styles from '../../styles/AIHelper.module.css';
import FaIcon from '../FaIcon';
import { trackAiHelperAuthRedirected, trackAiHelperRequestClicked, trackAuthCtaClicked } from '../../analytics';

/* -------------------------------------------------------------------------- */

function MessageBubble({ children }) {
  return (
    <div className={styles.messageBubble}>
      <div className={styles.avatarWrapper}>
        <img src="/lella.png" alt="Lella" className={styles.messageAvatar} />
      </div>
      <div className={styles.messageContent}>
        <span className={styles.messageAuthor}>Lella</span>
        <div className={styles.messageBody}>{children}</div>
      </div>
    </div>
  );
}

function LoadingDots() {
  return (
    <MessageBubble>
      <div className={styles.typingDots}>
        <span className={styles.typingDot} style={{ animationDelay: '0ms' }} />
        <span className={styles.typingDot} style={{ animationDelay: '150ms' }} />
        <span className={styles.typingDot} style={{ animationDelay: '300ms' }} />
      </div>
    </MessageBubble>
  );
}

function ErrorDisplay({ error }) {
  if (!error) return null;

  const safeMessage =
    typeof error.message === 'string' ? error.message : 'Si è verificato un errore.';

  const messages = {
    INSUFFICIENT_BALANCE: 'Gettoncini insufficienti per questa richiesta.',
    LLM_ERROR: 'Si è verificato un errore temporaneo. Riprova tra poco.',
    NETWORK_ERROR: 'Errore di rete. Controlla la tua connessione.',
    VALIDATION_ERROR: safeMessage || 'Richiesta non valida.',
    AI_HELPER_TIMEOUT: safeMessage || 'La risposta sta impiegando più del previsto.',
    HTTP_ERROR: safeMessage || 'Errore del server.',
  };

  return (
    <div className={styles.error}>
      <FaIcon className="fa-solid fa-circle-exclamation"  />
      <span>{messages[error.error] || safeMessage}</span>
    </div>
  );
}

const MarkdownResponse = React.memo(function MarkdownResponse({ text }) {
  return <ReactMarkdown>{text}</ReactMarkdown>;
});

/* -------------------------------------------------------------------------- */

function ConfirmationSection({ selectedText, selection, gameSlug, onConfirm }) {
  const { isSignedIn } = useUser();
  const { balance, getAIHelperCost } = useGettoncini();
  const navigate = useNavigate();
  const location = useLocation();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const aiHelperCost = getAIHelperCost();
  const canAfford = aiHelperCost == null || balance >= aiHelperCost;
  const hasInsufficientBalance = isSignedIn && !canAfford;
  const normalizedBalance = Number.isFinite(Number(balance)) ? Number(balance) : 0;
  const normalizedCost = Number.isFinite(Number(aiHelperCost)) ? Number(aiHelperCost) : 0;

  const handleAuthRedirect = useCallback(() => {
    const authPath = hasEverAuthenticated ? '/signin' : '/signup';
    const nextPath = location.pathname + (location.search || '');
    const ctaKind = hasEverAuthenticated ? 'sign_in' : 'sign_up';

    trackAiHelperAuthRedirected({
      source_surface: 'ai_helper_panel',
      gate_reason: 'ai_helper_requires_auth',
      game_slug: gameSlug || null,
      is_returning_device: hasEverAuthenticated,
      next_path: nextPath,
    });

    trackAuthCtaClicked({
      source_surface: 'ai_helper_panel',
      gate_reason: 'ai_helper_requires_auth',
      cta_kind: ctaKind,
      game_slug: gameSlug || null,
      is_returning_device: hasEverAuthenticated,
      next_path: nextPath,
    });

    const params = new URLSearchParams();
    const message = hasEverAuthenticated
      ? 'Accedi al tuo account per usare l\'assistente AI.'
      : 'Crea un account gratuito per usare l\'assistente AI.';
    params.set('message', message);
    params.set('next', nextPath);
    params.set('source', 'ai_helper_panel');
    params.set('gate_reason', 'ai_helper_requires_auth');
    navigate(`${authPath}?${params.toString()}`);
  }, [gameSlug, hasEverAuthenticated, location.pathname, location.search, navigate]);

  const handleConfirmClick = useCallback(() => {
    trackAiHelperRequestClicked({
      source_surface: 'ai_helper_panel',
      game_slug: gameSlug || null,
      selection_length: (selectedText || '').length,
      selection_component: selection?.component || null,
      selection_param_count: selection ? Math.max(Object.keys(selection).length - 1, 0) : 0,
      balance_before: balance,
    });
    onConfirm();
  }, [balance, gameSlug, onConfirm, selectedText, selection]);

  return (
    <div className={styles.confirmSection}>
      {hasInsufficientBalance ? (
        <div className={`${styles.error} ${styles.confirmError}`} role="alert">
          <div className={styles.confirmErrorMessage}>
            <FaIcon className="fa-solid fa-circle-exclamation"  />
            <span>
              {`Hai ${normalizedBalance} gettoncini. Per ottenere gettoncini extra ed utilizzare Lella, guarda un breve annuncio.`}
            </span>
          </div>
          <RewardedGettonciniCard
            sourceSurface="ai_helper_panel"
            gameSlug={gameSlug || null}
            compact
            className={styles.confirmErrorRewardedCta}
          />
        </div>
      ) : null}

      <p className={styles.confirmDescription}>
        Lella ti spiegherà il significato e il contesto di questa selezione nel gioco.
      </p>

      <div className={styles.confirmActions}>
        {!isSignedIn ? (
          <button
            className={styles.confirmButton}
            onClick={handleAuthRedirect}
          >
            <span>Registrati per provarla</span>
          </button>
        ) : (
          <div className={styles.confirmButtonWrap}>
            <button
              className={`${styles.confirmButton} ${!canAfford ? styles.confirmDisabled : ''}`}
              onClick={handleConfirmClick}
              disabled={!canAfford}
            >
              <span>Chiedi a Lella</span>
            </button>
            {aiHelperCost ? (
              <GettonciniCostChip amount={aiHelperCost} compact className={styles.confirmCostChip} />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

export default function AIHelperPanel() {
  const {
    isOpen,
    isConfirming,
    isLoading,
    response,
    error,
    selectedText,
    selection,
    puzzleInstanceId,
    gameSlug,
    requestHelp,
    dismiss,
  } = useAIHelper();

  const handleConfirm = useCallback(() => {
    if (!selection || !puzzleInstanceId) return;
    requestHelp(puzzleInstanceId, selection, selectedText);
  }, [selection, puzzleInstanceId, selectedText, requestHelp]);

  if (!isOpen) return null;

  const shouldShowBalanceRecovery = error?.error === 'INSUFFICIENT_BALANCE';
  const showConfirmationSection = isConfirming || shouldShowBalanceRecovery;

  return (
    <>
      {/* Backdrop for mobile */}
      <div className={styles.backdrop} onClick={dismiss} />

      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.panelHeader}>
          <div className={styles.headerContent}>
            <FaIcon className="fa-solid fa-wand-magic-sparkles"  />
            <span className={styles.headerTitle}>Scopri con Lella</span>
          </div>
          <button
            className={styles.closeButton}
            onClick={dismiss}
            aria-label="Chiudi"
          >
            <FaIcon className="fa-solid fa-xmark"  />
          </button>
        </div>

        {/* Selected text pill */}
        {selectedText && (
          <div className={styles.selectedTextPill}>
            <span className={styles.pillLabel}>Selezione:</span>
            <span className={styles.pillText}>{selectedText}</span>
          </div>
        )}

        {/* Confirmation prompt — shown before the request is sent */}
        {showConfirmationSection && (
          <ConfirmationSection
            selectedText={selectedText}
            selection={selection}
            gameSlug={gameSlug}
            onConfirm={handleConfirm}
          />
        )}

        {/* Response area — shown once the request has been initiated */}
        {!showConfirmationSection && (
          <div className={styles.responseArea}>
            {isLoading && <LoadingDots />}
            {error && <ErrorDisplay error={error} />}
            {response && (
              <MessageBubble>
                <div className={styles.responseText}>
                  <MarkdownResponse text={response} />
                </div>
              </MessageBubble>
            )}
          </div>
        )}
      </div>
    </>
  );
}
